import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Seo from '../components/Seo';
import { api } from '../lib/api';
import { brand } from '../config/brand';

const traits = [
  {
    k: '01',
    title: 'Living biological actives',
    body: 'Fermented bioenzymes and plant-derived surfactants that keep working after application — digesting organic soils at the molecular level rather than just wiping them around.',
  },
  {
    k: '02',
    title: 'Engineered, not diluted',
    body: 'Our most advanced formulation platform: triple-enzyme complexes, allergen-screened profiles, and skin-neutral chemistry developed to the highest tier we make.',
  },
  {
    k: '03',
    title: 'Batch-traceable provenance',
    body: 'Every production run carries a traceable batch identity under our ISO 9001:2015 system — serialized, documented, and consistent lot to lot.',
  },
  {
    k: '04',
    title: 'Transparent by design',
    body: 'Full ingredient disclosure with the purpose of every component. Nothing hidden behind “fragrance” or vague “active” labels.',
  },
];

export default function FlagshipPage() {
  const { data: products = [] } = useQuery({ queryKey: ['products'], queryFn: api.listProducts });
  const flagship = products.filter((p) => p.active && p.partitionKey === 'kids').slice(0, 6);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Uclipse Bioenzyme — The Flagship Line',
    url: `${brand.siteUrl}/flagship`,
    description:
      'Uclipse Bioenzyme is our crowning formulation platform — living biological actives that digest soils at the molecular level, batch-traceable and built to the highest tier.',
    isPartOf: { '@type': 'WebSite', name: 'Uclipse', url: `${brand.siteUrl}/` },
  };

  return (
    <>
      <Seo
        title="Uclipse Bioenzyme — The Flagship Line"
        description="Our crowning formulation platform. Uclipse Bioenzyme uses living biological actives that digest soils at the molecular level — batch-traceable, transparent, and built to the highest tier."
        canonicalUrl={`${brand.siteUrl}/flagship`}
        jsonLd={jsonLd}
      />

      {/* Dark premium hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage:
              'radial-gradient(60% 80% at 80% 0%, rgba(14,91,168,0.55) 0%, rgba(2,6,23,0) 60%), radial-gradient(50% 60% at 0% 100%, rgba(123,193,66,0.18) 0%, rgba(2,6,23,0) 60%)',
          }}
        />
        <div className="container-page relative py-20 md:py-28">
          <div className="flex items-center gap-3 font-mono text-[0.7rem] uppercase tracking-[0.35em] text-brand-200/80">
            <span className="h-px w-8 bg-brand-300/50" />
            Batch · UBE-2026 · Flagship
          </div>
          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.2em] text-brand-200">The flagship line</p>
          <h1 className="mt-3 font-display text-5xl font-extrabold leading-[1.02] tracking-tight md:text-7xl">
            Uclipse <span className="text-brand-300">Bioenzyme</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-slate-300">
            The crowning achievement of the Uclipse laboratory — a biological cleaning platform engineered to the
            Molecular Purity Standard and reserved for our most demanding applications.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/kid-safe" className="btn bg-white text-slate-900 hover:bg-slate-100">
              See it in the Kid-Safe range
            </Link>
            <Link
              to="/molecular-purity"
              className="btn bg-white/10 text-white ring-1 ring-white/25 hover:bg-white/20"
            >
              The science behind it
            </Link>
          </div>
        </div>
      </section>

      {/* Traits */}
      <section className="bg-slate-900 py-16 text-white">
        <div className="container-page grid gap-x-10 gap-y-10 md:grid-cols-2">
          {traits.map((t) => (
            <div key={t.k} className="flex gap-5 border-t border-white/10 pt-6">
              <div className="font-display text-2xl font-bold text-brand-300">{t.k}</div>
              <div>
                <h3 className="font-display text-lg font-semibold">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Product showcase */}
      {flagship.length > 0 && (
        <section className="bg-slate-950 pb-20 pt-4 text-white">
          <div className="container-page">
            <div className="mb-8 flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold md:text-3xl">The flagship range</h2>
              <Link to="/kid-safe" className="hidden text-sm font-semibold text-brand-300 hover:underline sm:inline">
                View the line →
              </Link>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {flagship.map((p, i) => (
                <Link
                  key={p.rowKey}
                  to={`/products/${encodeURIComponent(p.rowKey)}`}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 p-5 transition-colors hover:border-brand-400/40"
                >
                  <div className="flex items-center justify-between font-mono text-[0.65rem] uppercase tracking-widest text-slate-500">
                    <span>Bioenzyme</span>
                    <span>№ {String(i + 1).padStart(2, '0')}</span>
                  </div>
                  {p.imageUrl && (
                    <img
                      src={p.imageUrl}
                      alt={p.name}
                      loading="lazy"
                      className="mx-auto my-5 h-40 w-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  )}
                  <h3 className="font-display text-base font-semibold">{p.name}</h3>
                  {p.tagline && <p className="mt-1 line-clamp-2 text-sm text-slate-400">{p.tagline}</p>}
                  <span className="mt-4 inline-block text-sm font-semibold text-brand-300 group-hover:underline">
                    View product →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-brand-700 py-16 text-white">
        <div className="container-page text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Specify the flagship for your facility</h2>
          <p className="mx-auto mt-3 max-w-2xl text-brand-50/90">
            Talk to our team about deploying the Uclipse Bioenzyme platform across your operation — with the
            Precision Lab Group configuring it to your surfaces and workflows.
          </p>
          <div className="mt-7 flex justify-center gap-3">
            <Link to="/precision-lab" className="btn bg-white text-brand-700 hover:bg-slate-100">
              Meet the Precision Lab Group
            </Link>
            <Link to="/contact" className="btn bg-accent-green text-white hover:bg-green-600">
              Request a Quote
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
